import { useEffect, useRef, useState } from 'react';
import { ViewModelEntity } from './ViewModelEntity';
import { ObjectRepository, PrimitiveValueRepository, TextRepository } from '@framework/repository';
import { RTDBPath, isValidId } from 'balus-common';
import { WorkspaceEntity, WorkspaceSetting } from '@workspace/domain/workspace';
import { canReadViewModel } from './canReadViewModel';
import { isViewModelSharingUserRoleType, ViewModelSharingUserRoleType } from './vo/ViewModelSharingUserRoleType';
import type {
  AuthSessionId,
  GroupId,
  UserId,
  ViewModelId,
  ViewModelIndividualPermissionRoleJSON,
  WorkspaceId,
  WorkspaceMemberRoleJSON,
} from 'balus-common';
import { useCurrentUserId } from '@framework/auth';
import { useCurrentSessionId } from '@framework/auth/authStatus';
import { GroupIpRestrictionCheckRequest } from '@group/AccessControl';

type Result = {
  workspace: WorkspaceEntity | null;
  viewModel: ViewModelEntity | null;
  accessible: boolean; // 対象ビューモデルに権限的にアクセス可能な時に true を返す
  loading: boolean; // ローディング中に true を返す
};

const Loading: Result = { workspace: null, viewModel: null, accessible: false, loading: true };
const NotFound: Result = { workspace: null, viewModel: null, accessible: false, loading: false };
const Accessible: Result = { workspace: null, viewModel: null, accessible: true, loading: false };

type Cleanup = () => void;

class CleanupManager {
  private cleanups = new Map<string, () => void>();

  add(name: string, cleanupFunc: () => void) {
    const cleanup = this.cleanups.get(name);
    if (cleanup) {
      cleanup();
    }
    this.cleanups.set(name, cleanupFunc);
  }

  getExecutor() {
    return () =>
      this.cleanups.forEach((func) => {
        func();
      });
  }
}

// 対象のビューモデルを閲覧できない場合に true を返す
const isInvisible = (
  workspaceSetting: WorkspaceSetting | null,
  sharingUserRole: string | null,
  workspaceMemberRole: WorkspaceMemberRoleJSON | null,
  viewModelTrashedAt: string | null,
  groupEnableIndividualViewModelPermissions: boolean,
  individualPermissionRole: ViewModelIndividualPermissionRoleJSON | null
): boolean => {
  return (
    workspaceSetting === null ||
    sharingUserRole === null ||
    !isViewModelSharingUserRoleType(sharingUserRole) ||
    !canReadViewModel({
      workspaceSetting,
      workspaceMemberRole,
      viewModelTrashedAt,
      viewModelSharingUserRoleType: ViewModelSharingUserRoleType.load(sharingUserRole),
      groupEnableIndividualViewModelPermissions,
      individualPermissionRole,
    })
  );
};

const fetchViewModelEntity = async (
  viewModelId: ViewModelId,
  currentUserId: UserId | null,
  currentSessionId: AuthSessionId | null,
  currentWorkspaceId: React.RefObject<string | null>,
  setState: (state: Result | ((prev: Result) => Result)) => void
): Promise<Cleanup> => {
  const cleanupManager = new CleanupManager();

  /**
   * 引数に指定された id が RTDB のパス階層で指定できない文字列パターンの場合には、
   * Not Found として取り扱う。
   */
  if (!isValidId(viewModelId)) {
    setState(NotFound);
    return cleanupManager.getExecutor();
  }

  // ローディング状態に遷移させる
  setState(Loading);

  // ビューモデルのグループIDは変化しないので、一度取得して完了で良い
  const groupId = await new TextRepository(RTDBPath.ViewModel.viewModelGroupIdPath(viewModelId)).get();

  // ビューモデルのワークスペースは別ワークスペースに移動することがあるので継続して監視できるようにする
  const workspaceIdRepo = new TextRepository(RTDBPath.ViewModel.viewModelWorkspaceIdPath(viewModelId));
  const workspaceId = await workspaceIdRepo.get();

  // ビューモデルの groupId, workspaceId プロパティの値が見つからない場合には Not Found 扱い
  if (!groupId || !workspaceId) {
    setState(NotFound);
    return cleanupManager.getExecutor();
  }

  currentWorkspaceId.current = workspaceId;

  let notFound = false;

  /**
   * 対象ビューモデル・エンティティを取得可能か、権限チェックを継続的に行う関数
   */
  const startListen = async (groupId: GroupId | null, workspaceId: WorkspaceId | null): Promise<void> => {
    if (!groupId || !workspaceId) {
      setState(NotFound);
      notFound = true;
      return;
    }

    const groupEnableIndividualViewModelPermissionsRepo = new PrimitiveValueRepository<boolean>(
      RTDBPath.Group.enableIndividualViewModelPermissionsPath(groupId)
    );

    const workspaceMemberRoleRepo = currentUserId
      ? new TextRepository<WorkspaceMemberRoleJSON>(RTDBPath.Workspace.memberRolePath(workspaceId, currentUserId))
      : null;
    const workspaceSettingRepo = new ObjectRepository(WorkspaceSetting, RTDBPath.Workspace.settingPath(workspaceId));
    const viewModelTrashedAtRepo = new TextRepository(RTDBPath.ViewModel.trashedAtPath(viewModelId));
    const sharingUserRoleRepo = new TextRepository(RTDBPath.ViewModel.sharingUserRolePath(viewModelId));
    const individualPermissionRoleRepo = currentUserId
      ? new TextRepository<ViewModelIndividualPermissionRoleJSON>(
          RTDBPath.ViewModel.individualPermissionPath(viewModelId, currentUserId)
        )
      : null;

    const groupEnableIndividualViewModelPermissions =
      (await groupEnableIndividualViewModelPermissionsRepo.get()) || false;
    const workspaceMemberRole = workspaceMemberRoleRepo ? await workspaceMemberRoleRepo.get() : null;
    const workspaceSetting = await workspaceSettingRepo.get();
    const viewModelTrashedAt = await viewModelTrashedAtRepo.get();
    const sharingUserRole = await sharingUserRoleRepo.get();
    const individualPermissionRole = individualPermissionRoleRepo ? await individualPermissionRoleRepo.get() : null;

    // 最初に取得した情報を用いて、閲覧可否の結果を出す
    if (
      isInvisible(
        workspaceSetting,
        sharingUserRole,
        workspaceMemberRole,
        viewModelTrashedAt,
        groupEnableIndividualViewModelPermissions,
        individualPermissionRole
      )
    ) {
      setState(NotFound);
      notFound = true;
    }

    const listenerCallback = (
      workspaceSetting: WorkspaceSetting | null,
      sharingUserRole: string | null,
      workspaceMemberRole: WorkspaceMemberRoleJSON | null,
      viewModelTrashedAt: string | null,
      groupEnableIndividualViewModelPermissions: boolean,
      individualPermissionRole: ViewModelIndividualPermissionRoleJSON | null
    ) => {
      if (
        isInvisible(
          workspaceSetting,
          sharingUserRole,
          workspaceMemberRole,
          viewModelTrashedAt,
          groupEnableIndividualViewModelPermissions,
          individualPermissionRole
        )
      ) {
        // 閲覧できない場合には、状態を Not Found に遷移させる
        setState(NotFound);
        notFound = true;
      } else {
        // 閲覧できない状態から、閲覧可能な状態に遷移した時には、ページ自体のリロードによって強制的に再描画を行う
        if (notFound) {
          notFound = false;
          window.location.reload();
        }
      }
    };

    // 閲覧可否の判定に必要な情報を継続的に監視し、状態変化があった場合に再読み込みを行う
    if (workspaceMemberRoleRepo) {
      workspaceMemberRoleRepo.addListener((role) =>
        listenerCallback(
          workspaceSetting,
          sharingUserRole,
          role,
          viewModelTrashedAt,
          groupEnableIndividualViewModelPermissions,
          individualPermissionRole
        )
      );
      cleanupManager.add('workspaceMemberRoleRepo', () => workspaceMemberRoleRepo.removeListener());
    }

    workspaceSettingRepo.addListener((setting) =>
      listenerCallback(
        setting,
        sharingUserRole,
        workspaceMemberRole,
        viewModelTrashedAt,
        groupEnableIndividualViewModelPermissions,
        individualPermissionRole
      )
    );
    cleanupManager.add('workspaceSettingRepo', () => workspaceSettingRepo.removeListener());

    viewModelTrashedAtRepo.addListener((trashedAt) =>
      listenerCallback(
        workspaceSetting,
        sharingUserRole,
        workspaceMemberRole,
        trashedAt,
        groupEnableIndividualViewModelPermissions,
        individualPermissionRole
      )
    );
    cleanupManager.add('viewModelTrashedAtRepo', () => viewModelTrashedAtRepo.removeListener());

    sharingUserRoleRepo.addListener((sharingUserRole) =>
      listenerCallback(
        workspaceSetting,
        sharingUserRole,
        workspaceMemberRole,
        viewModelTrashedAt,
        groupEnableIndividualViewModelPermissions,
        individualPermissionRole
      )
    );
    cleanupManager.add('sharingUserRoleRepo', () => sharingUserRoleRepo.removeListener());

    groupEnableIndividualViewModelPermissionsRepo.addListener((newValue) => {
      listenerCallback(
        workspaceSetting,
        sharingUserRole,
        workspaceMemberRole,
        viewModelTrashedAt,
        newValue || false, // groupEnableIndividualViewModelPermissions
        individualPermissionRole
      );
    });
    cleanupManager.add('groupEnableIndividualViewModelPermissionsRepo', () =>
      groupEnableIndividualViewModelPermissionsRepo.removeListener()
    );

    if (individualPermissionRoleRepo) {
      individualPermissionRoleRepo.addListener((newValue) => {
        listenerCallback(
          workspaceSetting,
          sharingUserRole,
          workspaceMemberRole,
          viewModelTrashedAt,
          groupEnableIndividualViewModelPermissions,
          newValue // individualPermissionRole
        );
      });
      cleanupManager.add('individualPermissionRoleRepo', () => individualPermissionRoleRepo.removeListener());
    }
  };

  await startListen(groupId, workspaceId);

  workspaceIdRepo.addListener(async (newWorkspaceId) => {
    if (
      newWorkspaceId !== null &&
      newWorkspaceId !== currentWorkspaceId.current &&
      currentUserId &&
      currentSessionId &&
      !notFound
    ) {
      currentWorkspaceId.current = newWorkspaceId;

      await startListen(groupId, newWorkspaceId);

      const wsRepo = new ObjectRepository(WorkspaceEntity, RTDBPath.Workspace.workspaceEntityPath(newWorkspaceId));
      const vmRepo = new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(viewModelId));
      const [workspace, viewModel] = await Promise.all([wsRepo.get(), vmRepo.get()]);

      setState({ workspace, viewModel, accessible: true, loading: false });
    }
  });
  cleanupManager.add('workspaceIdRepo', () => workspaceIdRepo.removeListener());

  if (notFound) {
    setState(NotFound);
    return cleanupManager.getExecutor();
  }

  // グループ単位のIPアドレス制限の可否チェックを行い、判定結果が出るまで待つ
  const groupIpRestrictionCheckRequestCleanup: (() => void)[] = [];
  const ipRestrictionCheckResult = await GroupIpRestrictionCheckRequest.listen(
    groupId,
    currentUserId,
    currentSessionId,
    (status, prevStatus) => {
      if (prevStatus === 'Allow' && status === 'Deny') {
        // アクセス許可から禁止に変化した時は、 Not Found 状態に変更する
        setState(NotFound);
        return;
      }

      if (prevStatus === 'Deny' && status === 'Allow') {
        // アクセス禁止から許可に変化した時は、画面自体をリロードする
        window.location.reload();
      }
    },
    groupIpRestrictionCheckRequestCleanup // 他でも使用している関数なので、敢えてインターフェースを変えたくないのでこのようにする
  );
  if (groupIpRestrictionCheckRequestCleanup.length > 0) {
    cleanupManager.add('groupIpRestrictionCheckRequest', groupIpRestrictionCheckRequestCleanup[0]);
  }

  if (ipRestrictionCheckResult === 'Deny') {
    setState(NotFound);
    return cleanupManager.getExecutor();
  }

  // 未ログイン状態ならば、「アクセス可能である」というフラグのみを返す
  if (!currentUserId || !currentSessionId) {
    setState(Accessible);
    return cleanupManager.getExecutor();
  }

  const wsRepo = new ObjectRepository(WorkspaceEntity, RTDBPath.Workspace.workspaceEntityPath(workspaceId));
  const vmRepo = new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(viewModelId));

  const [workspace, viewModel] = await Promise.all([wsRepo.get(), vmRepo.get()]);

  // ワークスペース、ビューモデルの一方が null の場合には、両方を null にしてロード完了
  if (workspace === null && viewModel === null) {
    setState(NotFound);
    return cleanupManager.getExecutor();
  }

  // 最初の取得完了のステート更新を行ってから、変更監視を始める
  setState({ workspace, viewModel, accessible: true, loading: false });

  wsRepo.addListener((workspace) => setState((prev) => ({ ...prev, workspace })));
  cleanupManager.add('wsRepo', () => wsRepo.removeListener());

  vmRepo.addListener((viewModel) =>
    setState(({ viewModel: prev, ...rest }) =>
      // ビューモデルの updatedAt 以外が一致しているならば、ビューモデル・エンティティはそのまま更新しない。
      // (updatedAt 以外に変更があった場合には、変更監視コールバックの新しいビューモデル・エンティティに置き換える)
      viewModel && prev && viewModel.isEqualExcludingUpdatedAt(prev)
        ? { ...rest, viewModel: prev }
        : { ...rest, viewModel }
    )
  );
  cleanupManager.add('vmRepo', () => vmRepo.removeListener());

  return cleanupManager.getExecutor();
};

export const useViewModelEntity = (viewModelId: ViewModelId): Result => {
  const [result, setResult] = useState<Result>(Loading);

  const currentUserId = useCurrentUserId();
  const currentSessionId = useCurrentSessionId();

  const currentWorkspaceId = useRef<string>(null);

  useEffect(() => {
    let cleanup: null | (() => void) = null;

    // ビューモデルとワークスペースの読み取り可否の権限チェックを行なってから、エンティティの取得・監視を行う。
    fetchViewModelEntity(viewModelId, currentUserId, currentSessionId, currentWorkspaceId, setResult).then(
      (f) => (cleanup = f)
    );

    return () => {
      cleanup?.();
    };
  }, [currentSessionId, currentUserId, viewModelId]);

  return result;
};
